import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useGetFile } from '@/hooks';

interface ScreenshotGallery {
  open: (filepath: string) => void;
}

const ScreenshotGalleryContext = createContext<ScreenshotGallery>({
  open: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components -- hook exported alongside its provider component
export const useScreenshotGallery = () => useContext(ScreenshotGalleryContext);

// ScreenshotGalleryProvider owns the full ordered list of screenshots in a
// conversation and a single shared lightbox. A thumbnail anywhere below calls
// open(filepath); the lightbox then lets you arrow through every screenshot in
// capture order to watch the agent's session play out.
export function ScreenshotGalleryProvider({
  screenshots,
  children,
}: {
  screenshots: string[];
  children: React.ReactNode;
}) {
  const [index, setIndex] = useState<number | null>(null);

  const open = (filepath: string) => {
    const i = screenshots.indexOf(filepath);
    if (i >= 0) setIndex(i);
  };
  const step = (delta: number) =>
    setIndex(i =>
      i === null ? i : Math.min(screenshots.length - 1, Math.max(0, i + delta))
    );

  return (
    <ScreenshotGalleryContext.Provider value={{ open }}>
      {children}
      {index !== null && screenshots[index] ? (
        <GalleryLightbox
          filepath={screenshots[index]}
          index={index}
          total={screenshots.length}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onClose={() => setIndex(null)}
        />
      ) : null}
    </ScreenshotGalleryContext.Provider>
  );
}

function GalleryLightbox({
  filepath,
  index,
  total,
  onPrev,
  onNext,
  onClose,
}: {
  filepath: string;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const { data: url } = useGetFile<string>({ name: filepath });
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPrev, onNext, onClose]);

  const arrowClass =
    'absolute top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 transition-colors hover:bg-black/60 hover:text-white';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-12"
      onClick={onClose}
    >
      <div className="absolute top-4 right-4 flex items-center gap-3">
        <span className="text-sm text-white/70">
          {index + 1} / {total}
        </span>
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="text-white/70 transition-colors hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="size-6" />
        </button>
      </div>

      {hasPrev ? (
        <button
          onClick={e => {
            e.stopPropagation();
            onPrev();
          }}
          className={`${arrowClass} left-4`}
          aria-label="Previous screenshot"
        >
          <ChevronLeftIcon className="size-8" />
        </button>
      ) : null}

      {url ? (
        <img
          src={url}
          alt="screenshot"
          className="max-h-full max-w-full rounded object-contain"
          onClick={e => e.stopPropagation()}
        />
      ) : null}

      {hasNext ? (
        <button
          onClick={e => {
            e.stopPropagation();
            onNext();
          }}
          className={`${arrowClass} right-4`}
          aria-label="Next screenshot"
        >
          <ChevronRightIcon className="size-8" />
        </button>
      ) : null}
    </div>
  );
}
